import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Edit } from "lucide-react";

export default function AdminQuestions() {
  const { testId } = useParams();
  const [test, setTest] = useState<any>(null);
  const [list, setList] = useState<any[]>([]);
  const [editing, setEditing] = useState<any>(null);
  const [open, setOpen] = useState(false);

  async function load() {
    const { data: t } = await supabase.from("tests").select("*").eq("id", testId).maybeSingle();
    setTest(t);
    const { data } = await supabase.from("test_questions").select("*").eq("test_id", testId).order("sort_order");
    setList(data || []);
  }
  useEffect(() => { if (testId) load(); }, [testId]);

  function newItem() {
    setEditing({ test_id: testId, question: "", question_type: "fill", options: [], correct_answer: "", explanation: null, sort_order: list.length, _optionsText: "" });
    setOpen(true);
  }

  async function save() {
    const opts = (editing._optionsText || "")
      .split("\n")
      .map((s: string) => s.trim())
      .filter(Boolean);
    const { id, _optionsText, ...rest } = {
      ...editing,
      options: opts,
      question_type: opts.length > 0 ? "mcq" : "fill",
      explanation: null,
    };
    const op = id ? supabase.from("test_questions").update(rest).eq("id", id) : supabase.from("test_questions").insert(rest);
    const { error } = await op;
    if (error) toast.error(error.message);
    else { toast.success("Saved"); setOpen(false); load(); }
  }

  async function del(id: string) {
    if (!confirm("Delete?")) return;
    await supabase.from("test_questions").delete().eq("id", id);
    load();
  }

  return (
    <div className="space-y-4">
      <Link to="/admin/tests" className="text-sm hover:underline">← Tests</Link>
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Questions - {test?.title}</h2>
        <Button onClick={newItem}>Add question</Button>
      </div>
      <div className="grid gap-2">
        {list.map((q, i) => (
          <Card key={q.id}>
            <CardContent className="p-3 flex items-center justify-between">
              <div>
                <div className="font-medium">{i + 1}. {q.question}</div>
                <div className="text-sm text-muted-foreground">Answer: {q.correct_answer}</div>
              </div>
              <div className="flex gap-2">
                <Button size="icon" variant="ghost" onClick={() => { setEditing({ ...q, _optionsText: Array.isArray(q.options) ? q.options.join("\n") : "" }); setOpen(true); }}><Edit className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => del(q.id)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit" : "New"} question</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Question</Label><Textarea value={editing.question} onChange={(e) => setEditing({ ...editing, question: e.target.value })} /></div>
              <div>
                <Label>Options (one per line - leave empty for text answer)</Label>
                <Textarea
                  rows={4}
                  placeholder={"Option A\nOption B\nOption C\nOption D"}
                  value={editing._optionsText || ""}
                  onChange={(e) => setEditing({ ...editing, _optionsText: e.target.value })}
                />
              </div>
              <div><Label>Correct answer</Label><Input value={editing.correct_answer} onChange={(e) => setEditing({ ...editing, correct_answer: e.target.value })} /></div>
              <Button onClick={save} className="w-full">Save</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
